import { SIDEBAR_SPRING } from '@src/components/sidebar/sidebarMotion';
import CloseIcon from '@src/svgs/CloseIcon';
import EmptyDetailsIllustration from '@src/svgs/EmptyDetailsIllustration';
import {
  getDashboardStoryDurationLabel,
  resolveDashboardStoryThumbnailUrl,
} from '@src/utility/story';
import { motion as fmMotion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import {
  getClipAspectRatio,
  getClipDurationLabel,
  getClipThumbnailDisplay,
} from './clipDisplay';

import type { ClipThumbnailDisplay } from './clipDisplay';
import type { DashboardStory, OtherVideo } from '@src/types.ts/story';
import type { FC } from 'react';

export type DetailsTarget =
  | { type: 'story'; story: DashboardStory }
  // The clip's story comes along so an unrendered clip can fall back to the
  // story's frame instead of showing nothing.
  | { type: 'clip'; clip: OtherVideo; story: DashboardStory };

type Props = {
  /** Whether the panel is slid open. May be open with no target (empty state). */
  isOpen: boolean;
  target: DetailsTarget | null;
  onClose: () => void;
};

function formatDate(value: string | number | undefined): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const date = new Date(typeof value === 'number' ? value : value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

type Row = { label: string; value: string };

// Keep only rows that actually have a value, so the panel never shows empty
// metadata (people/date fields are sparse — clips carry editor/publisher,
// stories only carry created/published dates).
const compact = (
  rows: Array<{ label: string; value: string | undefined }>,
): Row[] => rows.filter((r): r is Row => Boolean(r.value));

function storyRows(story: DashboardStory): Row[] {
  const isPublished =
    !!story.finalVideo || (story.otherVideos?.length ?? 0) > 0;
  return compact([
    { label: 'Storyteller', value: story.storyTeller?.name },
    { label: 'Status', value: isPublished ? 'Published' : 'Draft' },
    { label: 'Duration', value: getDashboardStoryDurationLabel(story) },
    { label: 'Created', value: formatDate(story._createdAt) },
    { label: 'Published', value: formatDate(story._publishedAt) },
  ]);
}

function clipRows(clip: OtherVideo): Row[] {
  const isPublished = !clip.isHidden && !!clip.videoFilePrimary;
  const status = clip.isHidden ? 'Hidden' : isPublished ? 'Published' : 'Draft';
  return compact([
    { label: 'Dimensions', value: getClipAspectRatio(clip) },
    { label: 'Duration', value: getClipDurationLabel(clip) || undefined },
    { label: 'Status', value: status },
    { label: 'Last updated by', value: clip.lastActionJson?.editor },
    { label: 'Last updated', value: formatDate(clip.lastActionJson?.date) },
    { label: 'Published by', value: clip.lastPublishJson?.publisher },
    { label: 'Published', value: formatDate(clip._publishedAt) },
    { label: 'Source', value: clip.sourcePlatform },
  ]);
}

const DetailsPanel: FC<Props> = ({ isOpen, target, onClose }) => {
  // Keep the last target rendered through the slide-out so content doesn't
  // vanish mid-animation when closing. While open we follow the live target
  // (null → empty state); while closing we keep the last one.
  const lastTargetRef = useRef<DetailsTarget | null>(null);
  if (target) lastTargetRef.current = target;
  const shown = isOpen ? target : lastTargetRef.current;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const title = shown
    ? shown.type === 'story'
      ? shown.story.title
      : shown.clip.title || 'Untitled clip'
    : 'Details';
  // A story's own frame fills the box: the dashboard query doesn't carry a
  // story's shape, so there is nothing to fit it to.
  const { url: thumbnail, fit: thumbnailFit }: ClipThumbnailDisplay = shown
    ? shown.type === 'story'
      ? { url: resolveDashboardStoryThumbnailUrl(shown.story), fit: 'cover' }
      : getClipThumbnailDisplay(
          shown.clip,
          resolveDashboardStoryThumbnailUrl(shown.story),
        )
    : { fit: 'cover' };
  const rows = shown
    ? shown.type === 'story'
      ? storyRows(shown.story)
      : clipRows(shown.clip)
    : [];

  // Non-modal: no backdrop and no scroll lock, so the story/clip list stays
  // interactive while the panel is open (selecting a row updates the content).
  return createPortal(
    <Panel
      $isOpen={isOpen}
      initial={false}
      animate={{ x: isOpen ? 0 : '100%' }}
      transition={SIDEBAR_SPRING}
      role="complementary"
      aria-label="Details"
      data-testid="details-panel"
    >
      <Header>
        <HeaderTitle title={title}>{title}</HeaderTitle>
        <CloseButton type="button" onClick={onClose} aria-label="Close details">
          <CloseIcon />
        </CloseButton>
      </Header>
      <Body>
        {shown ? (
          <>
            {thumbnail && (
              <Thumb>
                <ThumbImg src={thumbnail} $fit={thumbnailFit} alt="" />
              </Thumb>
            )}
            <SectionTitle>Details</SectionTitle>
            <Rows>
              {rows.map(row => (
                <RowLine key={row.label}>
                  <RowLabel>{row.label}</RowLabel>
                  <RowValue>{row.value}</RowValue>
                </RowLine>
              ))}
            </Rows>
          </>
        ) : (
          <EmptyState>
            <EmptyArt>
              <EmptyDetailsIllustration />
            </EmptyArt>
            <EmptyText>Select a story or clip to see its details.</EmptyText>
          </EmptyState>
        )}
      </Body>
    </Panel>,
    document.body,
  );
};

export default DetailsPanel;

// Open/close is driven by Framer Motion's SIDEBAR_SPRING (via `animate` /
// `transition` on the element) so it feels identical to the main sidebar —
// the transform is animated by Motion, not a CSS transition.
const Panel = styled(fmMotion.div)<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 360px;
  max-width: 90vw;
  height: 100vh;
  background: var(--background);
  border-left: 1px solid var(--border);
  z-index: 1000010;
  display: flex;
  flex-direction: column;
  box-shadow: var(--elevation-lg);
  /* Slid out: don't intercept clicks meant for the list behind it. */
  pointer-events: ${p => (p.$isOpen ? 'auto' : 'none')};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border);
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-family: var(--font-primary);
  font-size: var(--text-md);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: var(--spacing-sm);
  border-radius: var(--radius-button);
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Keep wheel/touch scroll inside the panel — never chain to the page. */
  overscroll-behavior: contain;
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

// Fixed widescreen footprint so the panel's layout doesn't jump between a
// landscape and a vertical selection.
const Thumb = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-card);
  overflow: hidden;
  background: var(--surface);
`;

// Matches the clip list: widescreen frames fill the box, taller ones are
// centred whole inside it rather than cropped to a close-up.
const ThumbImg = styled.img<{ $fit: 'cover' | 'contain' }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $fit }) => $fit};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`;

const RowLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: 7px 0;
`;

const RowLabel = styled.span`
  flex-shrink: 0;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-muted);
`;

const RowValue = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  text-align: right;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Shown when the panel is open with nothing selected.
const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  text-align: center;
  padding: var(--spacing-lg);
`;

const EmptyArt = styled.div`
  width: 100%;
  max-width: 200px;
`;

const EmptyText = styled.p`
  margin: 0;
  max-width: 220px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-muted);
`;
